// New version src/Common/notification/notification.service.ts CommonNotificationService
// Old version src/CaseDotStar.ServicePackages.Frontend.Common/scripts/common/services/notification_service.js NotificationService

import {
	Injectable,
	SecurityContext,
} from '@angular/core';

import {
	find,
	forEach,
	get,
	has,
	includes,
	invoke,
	isArray,
	isFunction,
	isObject,
	map,
	mergeWith,
	reduce,
	remove,
	uniqueId,
} from 'lodash';
import Dotdotdot from 'dotdotdot-js';

import { commonConstants } from '../constants/common.constants';
import { commonBrowserService } from './browser.service';
import { CommonLocaleService } from './locale/locale.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ICommonResponseError } from '../@types/core';

declare interface INoty {
	id: string;
	shown: boolean;
	queued: boolean;
	timer: number | null;
	$element: JQuery<HTMLElement>;
	$body: JQuery<HTMLElement>;
	$buttons: JQuery<HTMLElement>;
	show: () => void;
	close: () => void;
	truncate: () => void;
}

declare interface ICommonNotyOptionsAnimation {
	open: string;
	close: string;
}

declare interface ICommonNotyOptionsButton {
	callback: (noty: INoty) => void;
	className: string;
	title: string;
}

declare interface ICommonNotyOptions {
	animation?: ICommonNotyOptionsAnimation;
	buttons?: ICommonNotyOptionsButton[];
	icon?: string;
	layout?: string;
	text?: string;
	timeout?: number;
	type?: string;
}

declare interface ICommonNotyCacheHeight {
	id: string;
	height: number;
}

declare interface ICommonNotyShowParams {
	iconType?: string;
	buttons?: ICommonNotyOptionsButton[];
}

@Injectable()
export class CommonNotificationService {
	private	NOTIFICATION_HEIGHT = 208;
	private	NOTIFICATION_TIMEOUT = 10000;

	private	notificationsQueue = {
		_queue: [] as INoty[],
		add: (noty: INoty) => {
			// eslint-disable-next-line no-underscore-dangle
			if (!find(this.notificationsQueue._queue, noty)) {
				// eslint-disable-next-line no-underscore-dangle
				this.notificationsQueue._queue.push(noty);
				noty.queued = true;
			}
			return this.notificationsQueue;
		},
		remove: (noty: INoty) => {
			noty.queued = false;
			// eslint-disable-next-line no-underscore-dangle
			remove(this.notificationsQueue._queue, (item) => item === noty);
			return this.notificationsQueue;
		},
		// eslint-disable-next-line no-underscore-dangle
		next: () => this.notificationsQueue._queue[0],
	};

	private animationStartEvents = 'webkitAnimationStart animationstart';
	private cacheHeight: ICommonNotyCacheHeight[] = [];
	private $body = $('body');
	private isIE = commonBrowserService.browser.ie;

	constructor (
		private commonLocaleService: CommonLocaleService,
		private domSanitizer: DomSanitizer
	) {}

	show (title = '', body: string | Record<string, unknown> = '', type: string, params: ICommonNotyShowParams = {}): void {
		const innerLayout = this.getMessage(title, body);

		setTimeout(() => {
			this.createNotification({
				text: innerLayout,
				type: invoke(type, 'toLowerCase'),
				icon: params.iconType ? 'icon_' + params.iconType : '',
				buttons: params.buttons,
			});
		}, 0);
	}

	handleResponseError (response: ICommonResponseError): void {
		const errorHeader = (get(response, 'data.ErrorHeader') || get(response, 'error.ErrorHeader')) || this.commonLocaleService.instant('common.noty.error.title');
		const	errorText = get(response, 'data.Error') || get(response, 'error.Error') || response.Error || '';
		const	errorTypes = ['General', 'Subscription', 'ConditionFailed'];
		let	errorType = get(response, 'data.ErrorType') || get(response, 'error.ErrorType');

		if (!includes(errorTypes, errorType)) {
			errorType = 'error';
		}

		errorType = errorType.toLowerCase();
		this.show(errorHeader, errorText, errorType);
	}

	private createNotificationLayout (noty: INoty, options: ICommonNotyOptions): JQuery<HTMLElement> {
		noty.$buttons.append(this.createNotificationButtons(noty, options.buttons!));
		noty.$body.append(options.text!);

		return noty.$element
			.addClass([
				'b-noty_bar',
				`b-noty_type__${options.type}`,
				`b-noty_theme__${options.icon}`,
			].join(' '))
			.append([noty.$body, noty.$buttons]);
	}

	private createNotificationButtons (noty: INoty, buttons: ICommonNotyOptionsButton[]): Array<JQuery<HTMLElement>>  {
		return map(buttons, (button) => {
			button.className = button.className || '';
			button.title = button.title || '';

			const $button: JQuery<HTMLElement> = $(`<button type='button' id='${ uniqueId('common_noty_button_') }' class='${ button.className }'>${ button.title }</button>`);

			if (isFunction(button.callback)) {
				$button.on('click', () => {
					button.callback(noty);
				});
			}

			return $button;
		});
	}

	private getMaxVisible (): number {
		return Math.floor(window.innerHeight / this.NOTIFICATION_HEIGHT);
	}

	private getContainer (options: ICommonNotyOptions): JQuery<HTMLElement> {
		let $notificationsContainer = this.$body.find('#common_noty_layout__' + options.layout);

		if ($notificationsContainer.length === 0) {
			$notificationsContainer = $(`<div id='common_noty_layout__${options.layout}'></div>`);
			if (this.isIE) {
				$notificationsContainer.addClass('b-noty_no-animate');
			}
			this.$body.append($notificationsContainer);
		}

		return $notificationsContainer;
	}

	private showNext (): void {
		// eslint-disable-next-line no-underscore-dangle
		forEach(this.notificationsQueue._queue, (noty) => {
			invoke(noty, 'show');
		});
	}

	private startClosingTimer (noty: INoty, options: ICommonNotyOptions): void {
		noty.timer = window.setTimeout(noty.close, options.timeout);
	}

	private endClosingTimer (noty: INoty): void {
		if (noty.timer) {
			clearTimeout(noty.timer);
			noty.timer = null;
		}
	}

	private onAnimationEnd (callback: () => void): () => void {
		return () => {
			setTimeout(callback, commonConstants.animationDuration);
		};
	}

	private animateAll (options: ICommonNotyOptions): void {
		const $notificationsContainer = this.getContainer(options);

		$notificationsContainer.children().each((elementIndex, element) => {
			const $element = $(element);
			const bottom = reduce(this.cacheHeight, (result, cacheItem, cacheIndex) => {
				if (cacheIndex > elementIndex) {
					return result + cacheItem.height!;
				} else {
					return result;
				}
			}, 0);

			$element.css({
				bottom,
			});
		});
	}

	private showNotification (noty: INoty, options: ICommonNotyOptions): INoty {
		let $notificationsContainer: JQuery<HTMLElement>;

		if (!noty.shown) {
			if (!noty.queued) {
				this.showNext();
				this.notificationsQueue.add(noty);
			}

			$notificationsContainer = this.getContainer(options);

			if ($notificationsContainer.children().length < this.getMaxVisible()) {
				$notificationsContainer.append(this.createNotificationLayout(noty, options));

				this.notificationsQueue.remove(noty);
				noty.truncate();
				this.cacheHeight.push({id: noty.id, height: noty.$element.outerHeight(true)!});

				this.animateAll(options);

				noty.shown = true;

				noty.$element
					.addClass(options.animation!.open)
					.on('mouseenter', () => {
						this.endClosingTimer(noty);
					})
					.on('mouseleave', () => {
						this.startClosingTimer(noty, options);
					})
					// in IE animationend does not fire sometimes
					// emulated animationend from animationstart to ANIMATE_DURATION
					.on(this.animationStartEvents, this.onAnimationEnd(() => {
						this.startClosingTimer(noty, options);
						noty.$element.removeClass(options.animation!.open);
						noty.$element.off(this.animationStartEvents);
					}));
			}
		}

		return noty;
	}

	private closeNotification (noty: INoty, options: ICommonNotyOptions): INoty {
		if (noty.shown) {
			this.endClosingTimer(noty);

			noty.$element
				.removeClass(options.animation!.open)
				.addClass(options.animation!.close)
				// in IE animationend does not fire sometimes
				// emulated animationend from animationstart to ANIMATE_DURATION
				.on(this.animationStartEvents, this.onAnimationEnd(() => {
					noty.shown = false;
					noty.$element.remove();
					remove(this.cacheHeight, (item) => item.id === noty.id);

					// eslint-disable-next-line no-underscore-dangle
					if (this.notificationsQueue._queue.length === 0) {
						this.animateAll(options);
					} else {
						this.showNext();
					}
				}));
		}

		return noty;
	}

	private createNotification (options: ICommonNotyOptions): void {
		const id = uniqueId('common_noty_bar_');

		const	defaults: ICommonNotyOptions = {
			text: '',
			type: 'success',
			layout: 'bottomLeft',
			icon: '',
			timeout: this.NOTIFICATION_TIMEOUT,
			animation: {
				open: 'slideInUp',
				close: 'slideOutLeftFade',
			},
			buttons: [
				{
					title: this.commonLocaleService.instant('common.dismiss'),
					className: 'b_noty_button_close',
					callback: (button) => {
						button.close();
					},
				},
			],
		};

		options = mergeWith({}, defaults, options, (objValue, srcValue) => {
			if (isArray(objValue) && isArray(srcValue)) {
				return objValue.concat(srcValue);
			}
		});

		const noty: INoty = {
			id,
			shown: false,
			queued: false,
			timer: null,
			$element: createNotificationElement(id),
			$body: $('<div class="b-noty_body"></div>'),
			$buttons: $('<div class="b-noty_buttons"></div>'),
			show: () => this.showNotification(noty, options),
			close: () => this.closeNotification(noty, options),
			truncate: () => {
				const element = document.querySelector('.b-noty-text-body--ellipsis');
				const option = {
					height: 160, // 10 text lines
				};
				new Dotdotdot(element as HTMLElement, option);
			},
		};

		noty.show();

		function createNotificationElement (notificationId: string) {
			const mobilePaddings = 32;
			const notificationNormalWidth = 384;
			const minViewportWidth = Math.min(window.innerWidth, window.screen.width);
			const width = minViewportWidth > notificationNormalWidth + mobilePaddings
				? notificationNormalWidth
				: minViewportWidth - mobilePaddings;

			return $(`<div id="${notificationId}" style="width: ${width}px;"></div>`);
		}
	}

	private getMessage (title = '', body: string | Record<string, unknown> = ''): string {
		let bodyEl = `<div class='b-noty-text-body b-noty-text-body--ellipsis'>${ body }</div>`;

		if (isObject(body) && has(body, 'template')) {
			// now use template as string instead of templateUrl
			// this using in administration
			bodyEl = `<div class='b-noty-text-body'>${ get(body, 'template') }</div>`;
		}

		const template = `
			<div class='b-noty-text-container'>
				<div class='b-noty-text-icon'></div>
				<div class='b-noty-text-info'>
						<div class='b-noty-text-title g-textoverflow' title='${ title }'>${ title }</div>
						${ bodyEl }
				</div>
			</div>
		`;

		return this.domSanitizer.sanitize(SecurityContext.HTML, template) || '';
	}
}
